import { API_ENDPOINTS, getApiUrl } from "../config";
import type { ConsumerLookupInfo } from "../view-models/ConsumerLookupInfo";
import type { FieldsForProductInfo } from "../view-models/FieldsForProductInfo";
import type { AuthConsumerInfo } from "../view-models/AuthConsumerInfo";
import { buildBffRequestHeaders } from "./bffRequestHeaders";
import {
  extractErrorDetailFromValidateConsumerResponse,
  flattenGffpFieldsByCategory,
  formatValidateConsumerErrorMessage,
  parseBffResponseText,
} from "../helper/sendValidationHelper";
import { withKioskLogContext } from "../helper/kioskLogContext";
import { useSessionStore } from "../../../state/src/useSessionStore";

function productFieldsForValidateConsumerError(): any[] {
  const state = useSessionStore.getState() as any;
  return [
    ...flattenGffpFieldsByCategory(state.gffpFieldsByCategory),
    ...(state.missingRequiredFields || []),
  ];
}

export const consumerLookup = async (
  phoneNumber: string,
  agentId: string,
  unitProfileId: string
): Promise<ConsumerLookupInfo> => {
    const payload = {
      AgentID: agentId,
      UnitProfileId: unitProfileId,
      ConsumerPhone: phoneNumber
    };
  
    const apiUrl = getApiUrl(API_ENDPOINTS.CONSUMER_LOOKUP);
    console.log('Making API call to ConsumerLookup with payload:', payload);
    console.log('API URL:', apiUrl);
  
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: buildBffRequestHeaders(),
        body: JSON.stringify(payload),
      });
  
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to lookup consumer: ${response.status} ${response.statusText}`);
      }
  
      const data = await response.json();
      console.log('API Response data:', data);

      // Some backends wrap real payload as JSON string in `data`
      // Example shape: { success: true, data: "{\"ErrorDetail\":{...}}", message: "..." }
      const possiblyWrapped = data as any;
      let parsed: any = possiblyWrapped;
      if (possiblyWrapped && typeof possiblyWrapped.data === 'string') {
        try {
          parsed = JSON.parse(possiblyWrapped.data);
        } catch {
          // Keep original if parse fails
          parsed = possiblyWrapped;
        }
      } else if (
        possiblyWrapped &&
        typeof possiblyWrapped.data === 'object' &&
        possiblyWrapped.data !== null &&
        !Array.isArray(possiblyWrapped.data)
      ) {
        // BFF may return { success, data: { SenderLookupInfos | ConsumerInfos, ... } }
        parsed = possiblyWrapped.data;
      }

      // If ErrorDetail exists, check ErrorCode 616 and surface a structured error
      const errorCode = parsed?.ErrorDetail?.ErrorCode;
      if (errorCode) {
        const structured: any = {
          name: 'ConsumerLookupError',
          message: parsed?.ErrorDetail?.ErrorString || 'Consumer lookup failed',
          code: errorCode,
          offendingField: parsed?.ErrorDetail?.OffendingField,
          raw: parsed
        };
        if (String(errorCode) === '616') {
          throw structured;
        }
      }

      return parsed as ConsumerLookupInfo;
    } catch (error) {
      console.error('Fetch error:', error);
      
      // If it's a CORS error, provide helpful information
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('CORS Error: Unable to connect to the API server. Make sure your backend server is running and configured to allow CORS requests from this origin.');
      }
      
      throw error;
    }
  }

  export const getFieldsForProductRequest = async (gffpData: any): Promise<FieldsForProductInfo> => {
    const url = getApiUrl(API_ENDPOINTS.GET_FIELDS_FOR_PRODUCT);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: buildBffRequestHeaders(),
        body: JSON.stringify(gffpData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const text = await response.text();
      let wrapper: any;
      try {
        wrapper = JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error('API returned invalid JSON');
      }

      if (wrapper && wrapper.success === false) {
        throw new Error(wrapper.error || 'Failed to load fields for product');
      }

      let payload: any = wrapper;
      if (wrapper && typeof wrapper.data === 'string') {
        try {
          payload = JSON.parse(wrapper.data);
        } catch (nestedParseError) {
          console.error('Failed to parse nested data JSON:', nestedParseError);
          throw new Error('API returned invalid nested JSON');
        }
      } else if (wrapper && typeof wrapper.data === 'object') {
        payload = wrapper.data;
      }

      // If backend embeds an error payload within a 200, surface it as an exception
      const embeddedError =
        payload?.ErrorDetail?.ErrorString ||
        payload?.ErrorDetail?.ErrorMessage ||
        payload?.message ||
        payload?.Message;
      if (embeddedError) {
        throw new Error(String(embeddedError));
      }

      // Normalize key casing from backend (often PascalCase) to our camelCase model
      const normalized: FieldsForProductInfo = {
        fqdoInfo: (payload as any).fqdoInfo ?? (payload as any).FqdoInfo,
        productFieldInfos: ((payload as any).productFieldInfos ?? (payload as any).ProductFieldInfos ?? []).map((f: any) => ({
          ...f,
          visibility: f.visibility ?? f.Visibility,
        })),
        dynamicFieldInfos: (payload as any).dynamicFieldInfos ?? (payload as any).DynamicFieldInfos,
      } as FieldsForProductInfo;

      return normalized;
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  }

  export const updateConsumerProfile = async (data: any): Promise<any> => {
    const url = getApiUrl(API_ENDPOINTS.UPDATE_CONSUMER_PROFILE);
 
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: buildBffRequestHeaders(),
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`UpdateConsumerProfile failed: ${response.status} ${response.statusText} - ${text}`);
      }
      const text = await response.text();
      let wrapper: any = {};
      try {
        wrapper = JSON.parse(text);
      } catch {
        return text;
      }
      let payload: any = wrapper;
      if (wrapper && typeof wrapper.data === 'string') {
        try {
          payload = JSON.parse(wrapper.data);
        } catch {
          payload = wrapper.data;
        }
      } else if (wrapper && typeof wrapper.data === 'object') {
        payload = wrapper.data;
      }
      return payload;
    } catch (error) {
      console.error('Fetch error:', error);
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('CORS Error: Unable to connect to the API server. Make sure your backend server is running and configured to allow CORS requests from this origin.');
      }
      throw error;
    }
  }
  
  export const validateConsumer = async (data: any): Promise<any> => {
    const url = getApiUrl(API_ENDPOINTS.VALIDATE_CONSUMER);
  
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: buildBffRequestHeaders(),
        body: JSON.stringify(withKioskLogContext(data)),
      });
      const text = await response.text();
      const wrapper = parseBffResponseText(text);

      if (wrapper && typeof wrapper === "object") {
        if (!response.ok || (wrapper as any).success === false) {
          const errDetail = extractErrorDetailFromValidateConsumerResponse(wrapper);
          throw new Error(
            errDetail
              ? formatValidateConsumerErrorMessage(
                  errDetail,
                  productFieldsForValidateConsumerError(),
                )
              : "Validation failed.",
          );
        }
        let payload: any = wrapper;
        if (typeof (wrapper as any).data === "string") {
          try {
            payload = JSON.parse((wrapper as any).data);
          } catch {
            payload = (wrapper as any).data;
          }
        } else if (
          typeof (wrapper as any).data === "object" &&
          (wrapper as any).data !== null
        ) {
          payload = (wrapper as any).data;
        }
        return payload;
      }

      if (!response.ok) {
        const parsed = parseBffResponseText(text);
        const errDetail = extractErrorDetailFromValidateConsumerResponse(parsed);
        throw new Error(
          errDetail
            ? formatValidateConsumerErrorMessage(
                errDetail,
                productFieldsForValidateConsumerError(),
              )
            : `ValidateConsumer failed: ${response.status} ${response.statusText}`,
        );
      }
      return text;
    } catch (error) {
      console.error('Fetch error:', error);
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('CORS Error: Unable to connect to the API server. Make sure your backend server is running and configured to allow CORS requests from this origin.');
      }
      throw error;
    }
  }

  export const postTransactionToKiosk = async (data: any): Promise<any> => {
    const url = getApiUrl(API_ENDPOINTS.POST_TRANSACTION_TO_KIOSK);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: buildBffRequestHeaders(),
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`postTransactionToKiosk failed: ${response.status} ${response.statusText} - ${text}`);
      }
      const text = await response.text();
      try {
        const parsed = JSON.parse(text);
        return typeof parsed.data === 'string' ? JSON.parse(parsed.data) : (parsed.data ?? parsed);
      } catch {
        return text;
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('CORS Error: Unable to connect to the API server. Make sure your backend server is running and configured to allow CORS requests from this origin.');
      }
      throw error;
    }
  }

  export const postAuthConsumer = async (data: any): Promise<AuthConsumerInfo> => {
    const url = getApiUrl(API_ENDPOINTS.AUTH_CONSUMER);
 
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: buildBffRequestHeaders(),
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`postAuthConsumer failed: ${response.status} ${response.statusText} - ${text}`);
      }
      const text = await response.text();
      let wrapper: any;
      try {
        wrapper = JSON.parse(text);
      } catch {
        throw new Error('API returned invalid JSON for postAuthConsumer');
      }
      if (wrapper && wrapper.success === false) {
        throw new Error(wrapper.error || wrapper.message || 'postAuthConsumer failed');
      }
      let payload: any = wrapper;
      if (wrapper && typeof wrapper.data === 'string') {
        try {
          payload = JSON.parse(wrapper.data);
        } catch {
          payload = wrapper.data;
        }
      } else if (wrapper && typeof wrapper.data === 'object') {
        payload = wrapper.data;
      }
      return payload as AuthConsumerInfo;
      }
    catch (error) {
      console.error('Fetch error:', error);
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('CORS Error: Unable to connect to the API server. Make sure your backend server is running and configured to allow CORS requests from this origin.');
      }
      throw error;
    }
  } 
  
  
  
  
